/**
 * The Rideboard's own terms (the user, 2026-10-21: "after the user has that
 * minimum data, he must sign an agreement to terms of use for the ride board
 * only, and it must be signed in the db").
 *
 * **The user's own text** (pasted 2026-10-21, "MyRave Rideboard Terms of Use"),
 * word for word — it replaced a first draft that never left dev. Every point is
 * a claim about the product: when the behaviour changes, this text changes in
 * the same commit — and so does `RIDE_TERMS_VERSION`.
 *
 * **A new version means everybody accepts again**: the API stores one row per
 * person per version (`travel.terms_acceptance`) and the Rideboard stays shut
 * until the CURRENT version is accepted. A typo fix that changes nothing
 * anyone agreed to may keep the version; anything else bumps it.
 *
 * The screens show this text through `tr()`, keyed — as every app string
 * is — by the exact English: the Hebrew is in the frontend's
 * `i18n/he/rideboard.ts`, and `rideboard.test.tsx` fails on a point without
 * it. The `key`s are React's, and stable.
 *
 * **Accepted and KEPT**: one `travel.terms_acceptance` row per account per
 * version, kept naming nobody when the account is deleted (backend
 * `travel/eligibility.ts`).
 */

/** The terms in force. `YYYY-MM-DD` — the day they were written. */
export const RIDE_TERMS_VERSION = '2026-10-21';

export interface RideTermsPoint { key: string; text: string }
export interface RideTermsSection {
  key: string;
  title: string;
  /** A line before the points ("If you offer a ride, you agree that:"). */
  lead?: string;
  points: RideTermsPoint[];
  /** A paragraph after the points. */
  after?: string;
}

export const RIDE_TERMS_TITLE = 'MyRave Rideboard Terms of Use';
/** The button that records the acceptance — the terms name it. */
export const RIDE_TERMS_AGREE = 'I Agree & Continue';
export const RIDE_TERMS_INTRO: readonly string[] = [
  'Effective date: 21 October 2026',
  'Last updated: 21 October 2026',
  'Please read these terms before using the MyRave Rideboard. By selecting “I Agree & Continue,” you agree to these Rideboard Terms of Use.',
];
/**
 * What a person confirms on the acceptance screen (the user, 2026-10-21: "a short summary above the full
 * terms") — EVERY one must be ticked, and the API stores which were (`confirmations`, the consent evidence).
 */
export const RIDE_TERMS_CONFIRMATIONS: readonly { key: string; text: string }[] = [
  { key: 'adult', text: 'I am 18 or older and my account details are accurate.' },
  { key: 'notGuaranteed', text: 'I understand that MyRave connects users but does not verify or guarantee rides.' },
  { key: 'rules', text: 'I will follow the safety, respect, honesty, and cost-sharing rules.' },
  { key: 'readTerms', text: 'I have read and agree to the Rideboard Terms of Use.' },
];
export const RIDE_TERMS_SUMMARY_TITLE = 'MyRave Rideboard';
export const RIDE_TERMS_SUMMARY_LEAD = 'Before you find a ride or offer seats, please confirm:';

export const RIDE_TERMS_CLOSING = 'Travel responsibly. Respect one another. Share the journey safely.';

const pts = (prefix: string, texts: string[]): RideTermsPoint[] => texts.map((text, i) => ({ key: `${prefix}${i + 1}`, text }));

export const RIDE_TERMS: readonly RideTermsSection[] = [
  {
    key: 'eligibility', title: '1. Eligibility and Account Information',
    points: pts('eligibility', [
      '1.1. You must be at least 18 years old to use the Rideboard.',
      '1.2. You confirm that the date of birth and mobile number associated with your account are accurate and that you are authorised to use the mobile number provided.',
      '1.3. You must provide accurate, current, and complete information when creating a ride post, requesting a ride, accepting a request, or communicating with another user.',
      '1.4. You must not impersonate another person, create misleading ride offers or requests, or use the Rideboard for unlawful purposes.',
      '1.5. MyRave may restrict or suspend Rideboard access where reasonably necessary to address suspected fraud, safety risks, misuse, or violations of these terms, subject to applicable law.',
    ]),
  },
  {
    key: 'role', title: '2. The Role of MyRave',
    points: pts('role', [
      '2.1. MyRave provides a digital platform that helps users discover and connect with other people who may wish to share a journey.',
      '2.2. MyRave is not a transport provider, taxi service, ride-hailing operator, travel agency, or party to any ride agreement between users. The Rideboard is intended for private, non-commercial cost-sharing arrangements, subject to applicable local law.',
      '2.3. Unless expressly stated otherwise, MyRave does not independently verify users\' identities, driving licences, driving records, vehicle condition, insurance coverage, or suitability for a particular journey.',
      '2.4. MyRave does not guarantee that a ride offer is genuine, that a driver or passenger will attend, that a journey will take place, or that another user will comply with their commitments.',
      '2.5. Users are responsible for deciding whether to interact with another user, accept or offer a ride, and proceed with a journey.',
      '2.6. MyRave may provide messaging, request management, seat allocation, reporting, and other coordination tools. These tools do not make MyRave a party to the ride agreement or a guarantor of a user\'s conduct.',
    ]),
  },
  {
    key: 'drivers', title: '3. Rules for Drivers', lead: 'By offering a ride, you represent and agree that:',
    points: pts('drivers', [
      '3.1. You hold a valid driving licence appropriate for the vehicle and journey.',
      '3.2. The vehicle is legally permitted to be used on the intended journey, is roadworthy, and has all legally required registration, inspection, and insurance coverage.',
      '3.3. You will comply with applicable traffic, road safety, passenger, insurance, and transport laws.',
      '3.4. You will not drive while impaired by alcohol, drugs, medication that makes driving unsafe, fatigue, or any other condition that makes you unfit to drive.',
      '3.5. You will accurately describe the journey, departure and arrival locations, date and time, vehicle, available seats, and expected cost contribution.',
      '3.6. You will not carry more passengers than the vehicle is legally or safely permitted to accommodate.',
      '3.7. You will promptly notify affected passengers of significant changes, delays, or cancellations.',
      '3.8. You will not use the Rideboard to offer unlawful commercial passenger transport or to generate profit from rides.',
    ]),
  },
  {
    key: 'passengers', title: '4. Rules for Passengers', lead: 'By requesting or accepting a ride, you agree that:',
    points: pts('passengers', [
      '4.1. You will provide accurate information about your destination, departure point, travel date, and number of passengers.',
      '4.2. You will communicate respectfully and agree on the pickup location, departure time, route, cost contribution, and other material arrangements before travelling.',
      '4.3. You will arrive at the agreed meeting point on time or notify the driver promptly if you are delayed.',
      '4.4. You will cancel as early as reasonably possible if you can no longer travel.',
      '4.5. You will not pressure a driver to drive unsafely, exceed passenger capacity, break the law, or accept unagreed changes to the journey.',
      '4.6. You may decline a ride or withdraw a request if you are uncomfortable with the proposed arrangements.',
    ]),
  },
  {
    key: 'costs', title: '5. Cost Sharing and Legal Compliance',
    points: pts('costs', [
      '5.1. The Rideboard is intended for sharing reasonable journey expenses, not for generating profit.',
      '5.2. Permitted contributions may include a reasonable share of fuel, tolls, parking, and other legitimate costs directly associated with the shared journey, where permitted by applicable law.',
      '5.3. Drivers and passengers are responsible for agreeing on the amount, calculation, and payment arrangements before the journey.',
      '5.4. Users must not use the Rideboard to operate an unlicensed transport business, evade transport regulations, or misrepresent a commercial service as private cost sharing.',
      '5.5. Users must comply with all applicable local requirements concerning passenger transport, insurance, licences, taxes, and cost-sharing arrangements.',
      '5.6. MyRave does not guarantee payment, reimbursement, or compensation between users. Unless a specific MyRave feature expressly provides otherwise, MyRave does not collect or hold ride contributions.',
    ]),
  },
  {
    key: 'seats', title: '6. Requests, Seat Allocation, and Confirmations',
    points: pts('seats', [
      '6.1. Sending or approving a ride request does not necessarily mean that a seat has been allocated or that the journey is finally confirmed.',
      '6.2. Users must check the status of their request and confirm the practical details directly with the other party before travelling.',
      '6.3. Drivers are responsible for confirming the number of seats they can provide. Passengers are responsible for checking that the allocation meets their needs.',
      '6.4. Users must not make false seat allocations, misrepresent availability, or deliberately reserve seats they do not intend to use.',
      '6.5. Changes to a seat allocation, journey, or agreement should be communicated promptly to all affected users.',
    ]),
  },
  {
    key: 'cancel', title: '7. Cancellations and Changes',
    points: pts('cancel', [
      '7.1. Users should cancel or update a ride post as soon as reasonably possible when their plans change.',
      '7.2. Drivers and passengers should notify one another promptly of cancellations, delays, or changes to the pickup location, route, or departure time.',
      '7.3. MyRave does not guarantee that a replacement ride will be available following a cancellation.',
      '7.4. Users are responsible for agreeing on any reimbursement or cancellation arrangements, subject to applicable law and any separately agreed terms.',
      '7.5. MyRave is not responsible for a user\'s failure to attend or perform an agreed journey, except to the extent responsibility cannot lawfully be excluded.',
    ]),
  },
  {
    key: 'safety', title: '8. Safety, Conduct, and Respect',
    points: pts('safety', [
      '8.1. Users must treat one another respectfully and must not engage in harassment, threats, discrimination, intimidation, coercion, stalking, or unwanted sexual conduct.',
      '8.2. Users must not pressure another person to share personal information, accept a ride, or engage in unwanted communication.',
      '8.3. Users should verify relevant journey details, meet in an appropriate public location where practical, inform someone they trust of their travel plans when appropriate, and use required seat belts and safety equipment.',
      '8.4. Do not enter or remain in a vehicle if you reasonably believe that the driver, vehicle, or circumstances are unsafe.',
      '8.5. Users should report suspected abuse, fraud, unsafe conduct, or violations of these terms through the available MyRave reporting tools.',
      '8.6. If you believe you are in immediate danger, contact the relevant local emergency services.',
      '8.7. Reporting an incident does not guarantee that MyRave can prevent harm, identify a user, recover losses, or resolve a dispute. MyRave may take reasonable action within its capabilities and legal obligations.',
    ]),
  },
  {
    key: 'privacy', title: '9. Privacy and Contact Details',
    points: pts('privacy', [
      '9.1. Users must not post phone numbers, email addresses, or external contact links in Rideboard posts or messages where MyRave\'s contact-sharing rules prohibit them.',
      '9.2. Users who wish to share supported personal contact information should use MyRave\'s Share Contact Details feature.',
      '9.3. Users are responsible for deciding what information they share. Shared information may include personal data that can identify or contact them.',
      '9.4. A user may revoke future access through MyRave\'s available sharing controls. Revocation cannot retrieve information that another user has already viewed, copied, photographed, downloaded, or otherwise retained.',
      '9.5. Users must not misuse another person\'s personal information, harass them outside the platform, or disclose their private information without appropriate authorisation or another lawful basis.',
      '9.6. MyRave\'s collection, use, storage, disclosure, and deletion of personal information are governed by its Privacy Policy and applicable data protection laws.',
    ]),
  },
  {
    key: 'prohibited', title: '10. Prohibited Use', lead: 'Users must not:',
    points: pts('prohibited', [
      '10.1. Use the Rideboard for unlawful commercial transport or any activity that violates applicable law.',
      '10.2. Publish fraudulent, misleading, abusive, discriminatory, or deliberately inaccurate content.',
      '10.3. Request payment or contributions through deceptive, coercive, or fraudulent means.',
      '10.4. Misuse the platform to spam, scam, threaten, harass, or exploit other users.',
      '10.5. Attempt to bypass account restrictions, manipulate requests or seat allocations, or interfere with the proper operation of the Rideboard.',
      '10.6. Use another user\'s information for unauthorised purposes.',
    ]),
  },
  {
    key: 'moderation', title: '11. Reports, Moderation, and Account Restrictions',
    points: pts('moderation', [
      '11.1. MyRave may review reports, investigate suspected violations, remove or restrict content, limit Rideboard functionality, or suspend or terminate accounts where reasonably necessary and permitted by law.',
      '11.2. MyRave may take urgent action where it reasonably considers that users, the platform, or others may face a safety, fraud, or security risk.',
      '11.3. Where appropriate and legally permitted, MyRave may provide notice of enforcement decisions and an opportunity to appeal.',
      '11.4. MyRave may cooperate with lawful requests from competent authorities where required by applicable law.',
      '11.5. Nothing in this section requires MyRave to monitor every ride, message, user, or journey.',
    ]),
  },
  {
    key: 'availability', title: '12. Platform Availability',
    points: pts('availability', [
      '12.1. MyRave aims to make the Rideboard available and functional but does not guarantee uninterrupted access, error-free operation, or the availability of particular users or rides.',
      '12.2. Features may be changed, suspended, or discontinued where reasonably necessary for maintenance, security, operational, or legal reasons, subject to applicable law.',
      '12.3. Users should not rely exclusively on the Rideboard for urgent travel arrangements or safety-critical communications.',
    ]),
  },
  {
    key: 'liability', title: '13. Responsibility and Liability',
    points: pts('liability', [
      '13.1. Users remain responsible for their own conduct, decisions, legal obligations, and agreements with other users.',
      '13.2. To the extent permitted by applicable law, MyRave is not responsible for the acts or omissions of users, the condition or operation of vehicles, the performance or cancellation of rides, or disputes arising directly between drivers and passengers.',
      '13.3. Nothing in these terms excludes or limits liability for fraud, wilful misconduct, death or personal injury caused by negligence, or any other liability or right that cannot lawfully be excluded or limited under applicable law.',
      '13.4. Nothing in these terms removes any mandatory consumer protection or other statutory rights that apply to you.',
      '13.5. Any limitation of MyRave\'s liability must be interpreted and applied only to the extent permitted by the law applicable to the relevant claim.',
    ]),
  },
  {
    key: 'changes', title: '14. Changes to These Terms',
    points: pts('changes', [
      '14.1. MyRave may update these terms to reflect changes in the Rideboard, security requirements, business operations, or applicable law.',
      '14.2. The current version and its effective date will be made available through MyRave.',
      '14.3. Where required by law, or where a material change requires renewed agreement, MyRave will provide appropriate notice and request acceptance before the affected functionality continues to be used.',
      '14.4. Previous versions and acceptance records may be retained where reasonably necessary for legal compliance, dispute resolution, security, and accountability, in accordance with applicable data protection laws.',
    ]),
  },
  {
    key: 'law', title: '15. Governing Law and Mandatory Rights',
    points: pts('law', [
      '15.1. These Rideboard Terms form part of the terms governing your use of the MyRave platform and should be read together with MyRave\'s general Terms of Service and Privacy Policy.',
      '15.2. The governing law, dispute resolution arrangements, and competent courts should be specified in MyRave\'s main Terms of Service after legal review of the operating entity and intended markets.',
      '15.3. Nothing in these terms deprives a user of mandatory legal protections or access to courts where such rights cannot lawfully be excluded or restricted.',
    ]),
  },
  {
    key: 'acceptance', title: '16. Acceptance', lead: 'By selecting “I Agree & Continue,” you confirm that:',
    points: pts('acceptance', [
      'You are at least 18 years old.',
      'The account information you have provided is accurate to the best of your knowledge.',
      'You have read and agree to these Rideboard Terms of Use.',
      'You understand that MyRave connects users but does not operate or guarantee individual rides.',
      'You agree to comply with applicable laws and these terms whenever you use the Rideboard.',
    ]),
  },
];

/* ---- The terms in another language (the user, 2026-10-27: "translate the legal texts into all languages") ----
 *
 * The English above is the user's own text and stays exactly as it is; a
 * translation is the same document in this shape — every section by its
 * `key`, every point in order, the confirmations by their keys. The screens
 * (`frontend/src/visitor/rideboard/RideGate.tsx`) and the `/terms` annex
 * (`frontend/worker/terms.ts`) draw the reader's language, and the English
 * where it has none. What is ACCEPTED does not change: the same
 * `RIDE_TERMS_VERSION` and the same confirmation keys, in any language. The
 * translations are the frontend's (`src/i18n/<language>/legal.ts`).
 */
export interface RideTermsSectionText {
  title: string;
  lead?: string;
  /** The section's points, in the English order. */
  points: readonly string[];
  after?: string;
}

export interface RideTermsText {
  title: string;
  agree: string;
  intro: readonly string[];
  /** By `RIDE_TERMS_CONFIRMATIONS` key. */
  confirmations: Readonly<Record<string, string>>;
  summaryTitle: string;
  summaryLead: string;
  closing: string;
  /** By `RIDE_TERMS` section key. */
  sections: Readonly<Record<string, RideTermsSectionText>>;
}

/** The English, in the translations' shape — built from the constants above, never typed twice. */
export const RIDE_TERMS_TEXT: RideTermsText = {
  title: RIDE_TERMS_TITLE,
  agree: RIDE_TERMS_AGREE,
  intro: RIDE_TERMS_INTRO,
  confirmations: Object.fromEntries(RIDE_TERMS_CONFIRMATIONS.map((c) => [c.key, c.text])),
  summaryTitle: RIDE_TERMS_SUMMARY_TITLE,
  summaryLead: RIDE_TERMS_SUMMARY_LEAD,
  closing: RIDE_TERMS_CLOSING,
  sections: Object.fromEntries(RIDE_TERMS.map((s) => [s.key, {
    title: s.title,
    ...(s.lead ? { lead: s.lead } : {}),
    points: s.points.map((p) => p.text),
    ...(s.after ? { after: s.after } : {}),
  }])),
};

/**
 * Where a translation differs from the English in SHAPE — a section, a
 * point, a lead or a confirmation missing or extra, a number changed. Empty
 * when the two are the same document.
 */
export function rideTermsShapeProblems(t: RideTermsText, en: RideTermsText = RIDE_TERMS_TEXT): string[] {
  const out: string[] = [];
  const num = (s: string) => /^(\d+(?:\.\d+)*)\.\s/.exec(s)?.[1] ?? null;
  const same = (a: string[], b: string[], what: string) => {
    if (a.join() !== b.join()) out.push(`${what}: ${a.join(',')} ≠ English ${b.join(',')}`);
  };
  if (t.intro.length !== en.intro.length) out.push(`intro: ${t.intro.length} lines, English ${en.intro.length}`);
  same(Object.keys(t.confirmations), Object.keys(en.confirmations), 'confirmations');
  same(Object.keys(t.sections), Object.keys(en.sections), 'sections');
  for (const [key, e] of Object.entries(en.sections)) {
    const s = t.sections[key];
    if (!s) continue;
    if (num(s.title) !== num(e.title)) out.push(`${key}: title number ${num(s.title)} ≠ ${num(e.title)}`);
    if (Boolean(s.lead) !== Boolean(e.lead)) out.push(`${key}: lead`);
    if (Boolean(s.after) !== Boolean(e.after)) out.push(`${key}: after`);
    if (s.points.length !== e.points.length) out.push(`${key}: ${s.points.length} points, English ${e.points.length}`);
    s.points.forEach((p, i) => {
      const n = e.points[i];
      if (n !== undefined && num(p) !== num(n)) out.push(`${key} point ${i + 1}: number ${num(p)} ≠ ${num(n)}`);
      if (!p.trim()) out.push(`${key} point ${i + 1}: empty`);
    });
  }
  for (const v of [t.title, t.agree, t.summaryTitle, t.summaryLead, t.closing, ...t.intro, ...Object.values(t.confirmations)]) {
    if (!v.trim()) out.push('an empty line');
  }
  return out;
}
